import { Box, Button, Divider, ITheme, SvgIcon, Typography, useTheme } from '@mui/material';
import React from 'react';
import LeoTag from '../common/LeoTag';
import StageDistributionGraph from '../common/StageDistributionGraph';
import { useTranslation } from 'react-i18next';
import GeographicDistributionGraph from '../common/GeographicDistributionGraph';
import PortfolioItemHeader from './PortfolioItemHeader';
import { PortfolioLibraryItem, PortfolioStatus } from '../../models/Portfolio';
import ProgressBar from '../common/ProgressBar';
import { Utilities } from '../../utility/utils';
import PortfolioPerformanceItem from './PorfolioPerformanceItem';
import { ReactComponent as slidersIcon } from '../../assets/sliders.svg'
import { ReactComponent as donationIcon } from '../../assets/donation.svg'
import { ReactComponent as stockLigthIcon } from '../../assets/stock_light.svg'
import { ReactComponent as stackLightIcon } from '../../assets/stack_light.svg'
import { ReactComponent as camemberIcon } from '../../assets/camembert.svg'
import { ReactComponent as worldIcon } from '../../assets/world.svg'
import { ReactComponent as swapIcon } from '../../assets/swap.svg'
import { ReactComponent as moneyIcon } from '../../assets/money_2.svg'
import { ReactComponent as propIcon } from '../../assets/prop.svg'
import { ReactComponent as stackIcon } from '../../assets/stack.svg'
import { ReactComponent as stockIcon } from '../../assets/stock.svg'
import { ReactComponent as moneybagIcon } from '../../assets/moneybag.svg'
import { ReactComponent as cogIcon } from '../../assets/cog.svg'
import PublishIcon from '@mui/icons-material/Publish';
import FundAllocationGraph from '../common/FundAllocationGraph';
import { useNavigate, useParams } from 'react-router-dom';
import { AUTHENTICATED_ROUTES } from '../../resources/routes-constants';
import { useAppDispatch } from '../../store/reducers/store';
import { addFundToPortfolio } from '../../store/actions/portfolioLibrary';

export interface IPortfolioItemProps {
    portfolio: PortfolioLibraryItem
    isAddToExisting?: boolean;
}

const PortfolioItem: React.FC<IPortfolioItemProps> = ({ portfolio, isAddToExisting }) => {
    const theme = useTheme() as ITheme;
    const { t, i18n } = useTranslation();
    const sectors = [...portfolio.sectorDistribution];
    sectors.sort((a, b) => b.proportion - a.proportion);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams();

    const openDetails = () => {
        let route = undefined;
        switch (portfolio.status) {
            case PortfolioStatus.Draft:
                route = AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_ROUTE;
                break;
            case PortfolioStatus.Live:
            case PortfolioStatus.Closed:
                route = AUTHENTICATED_ROUTES.PORTFOLIO_DETAILS_ROUTE;
                break;
            case PortfolioStatus.Pending:
                route = AUTHENTICATED_ROUTES.PORTFOLIO_TRACKING_ROUTE;
                break;
        }
        if (route === undefined) {
            return;
        }
        if (isAddToExisting) {
            dispatch(addFundToPortfolio({ id: portfolio.id, fundId: parseInt(id as string) })).then(() => {
                navigate(AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_STEP_ROUTE.replace(":id", `${portfolio.id}`).replace(":step", '3'));
            })
        } else {
            navigate(route.replace(":id", `${portfolio.id}`));
        }
    }

    const openPublish = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(AUTHENTICATED_ROUTES.PORTFOLIO_EDIT_STEP_ROUTE.replace(":id", `${portfolio.id}`).replace(":step", "5"));
    }

    const renderTitle = (title: string, icon: any): React.ReactNode => {
        return <Box display={"flex"} gap={"10px"} alignItems={"center"}>
            <SvgIcon component={icon} inheritViewBox style={{ width: "18px", height: "18px" }} />
            <Typography fontWeight={"500"} fontSize={"14px"} lineHeight={"17.07px"} color={theme.palette.leo.type[900]}>{title}</Typography>
        </Box>
    }

    return <Box display={'flex'} flexDirection={"column"} flexGrow={1} bgcolor={theme.palette.common.white} padding={"19px 20px"} borderRadius={"8px"} gap={"16px"} boxShadow={theme.shadows[2]} flexBasis={"30%"} maxWidth={"32%"} onClick={openDetails} sx={{ cursor: "pointer" }}>
        
        <Box height={"22px"} marginTop={"-29px"} display={"flex"} gap={"4px"}>
            {portfolio.isEltif && (portfolio.isCompliant ? <Box height={"22px"} width={"auto"} padding={"0 3px"} bgcolor={theme.palette.primary.dark} alignSelf={"self-start"} borderRadius={"4px"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Typography color={theme.palette.common.white} fontSize={"11px"} lineHeight={"20px"} fontWeight={"600"}>{t('PortfolioPageELTIFCompliant')}</Typography>
            </Box>
            :
            <Box height={"22px"} width={"auto"} padding={"0 3px"} bgcolor={theme.palette.leo.color2} alignSelf={"self-start"} borderRadius={"4px"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Typography color={theme.palette.common.white} fontSize={"11px"} lineHeight={"20px"} fontWeight={"600"}>{t('PortfolioPageELTIFNonCompliant')}</Typography>
            </Box>
            )}
        </Box>
        <PortfolioItemHeader id={portfolio.id} state={portfolio.status} closingDate={portfolio.closingDate} title={portfolio.name} isAddToExisting={isAddToExisting} />
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
            <Typography fontWeight={"700"} fontSize={"20px"} lineHeight={"24.38px"} letterSpacing={"0.5px"}>{portfolio.name}</Typography>
            <Box display={"flex"} flexDirection={"column"} alignItems={"end"}>
                <Typography fontWeight={"600"} fontSize={"20px"} lineHeight={"24.38px"} letterSpacing={"0.5px"}>{`€${Utilities.toFriendlyMoneyValue(portfolio.fundraisingTarget)}`}</Typography>
                <Typography fontWeight={"600"} fontSize={"10px"} lineHeight={"10px"} color={theme.palette.leo.type[400]}>{portfolio.status === PortfolioStatus.Closed ? t('PortfolioPageItemCapitalRaised') : t('PortfolioPageItemFundraisingTarget')}</Typography>
            </Box>
        </Box>
        {portfolio.status === PortfolioStatus.Live && <Box display={"flex"} flexDirection={"column"}>
            <ProgressBar proportion={portfolio.raisedAmount / portfolio.fundraisingTarget} />
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} marginTop={"8px"}>
                <Box display={"flex"} gap={"2px"} alignItems={"center"}>
                    <Typography fontSize={"12px"} lineHeight={"14.63px"} fontWeight={"600"} color={theme.palette.leo.type[600]}>{Math.floor(100 * portfolio.raisedAmount / portfolio.fundraisingTarget)}%</Typography>
                    <Typography fontSize={"10px"} lineHeight={"10px"} fontWeight={"600"} color={theme.palette.leo.type[600]}>{t('PortfolioPageItemCompleted')}</Typography>
                    <Typography fontSize={"12px"} lineHeight={"14.63px"} fontWeight={"600"} color={theme.palette.leo.type[600]}>| €{Utilities.toFriendlyMoneyValue(portfolio.raisedAmount)}</Typography>
                </Box>
                <Box display={"flex"} gap={"11px"}>
                    <Typography fontSize={"14px"} lineHeight={"12px"} fontWeight={"600"} color={theme.palette.leo.type[600]}>{t('PortfolioPageItemTVPI')}: {(portfolio.tvpi ? portfolio.tvpi : 0).toFixed(1)}</Typography>
                    <Typography fontSize={"14px"} lineHeight={"12px"} fontWeight={"600"} color={theme.palette.leo.type[600]}>{t('PortfolioPageItemDPI')}: {portfolio.dpi}</Typography>
                </Box>
            </Box>
        </Box>}
        <Divider sx={{ margin: "0 -19px" }} />
        <Box display={"flex"} flexDirection={"column"} gap={"16px"} justifyContent={"space-between"} flexGrow={1}>

            {portfolio.status === PortfolioStatus.Closed ?
                <>
                    {renderTitle(t('PortfolioPageItemPerformanceMetrics'), stockLigthIcon)}
                    <Box display={"flex"} flexWrap={"wrap"} gap={"20px"}>
                        <PortfolioPerformanceItem name={t('PortfolioPageItemTVPI')} value={`${(portfolio.tvpi ? portfolio.tvpi : 0).toFixed(1)}`} icon={swapIcon} />
                        <PortfolioPerformanceItem name={t('PortfolioPageItemDPI')} value={`${portfolio.dpi}`} icon={moneyIcon} />
                        {portfolio.irr && <PortfolioPerformanceItem name={t('PortfolioPageItemIRR')} value={`${Math.floor(portfolio.irr * 100)}%`} icon={propIcon} />}
                        <PortfolioPerformanceItem name={t('PortfolioPageItemTVPI')} value={`€${(portfolio.tvpi ? portfolio.tvpi : 0).toLocaleString(i18n.language)}`} icon={stackIcon} />
                    </Box>
                    <Divider />
                    {renderTitle(t('PortfolioPageItemRevenueGenerated'), stackLightIcon)}
                    <Box display={"flex"} flexWrap={"wrap"} gap={"20px"}>
                        <PortfolioPerformanceItem name={t('PortfolioPageItemManagementFee')} value={`€${portfolio.managementFee?.toLocaleString(i18n.language)}`} icon={cogIcon} />
                        <PortfolioPerformanceItem name={t('PortfolioPageItemCarryFee')} value={`€${portfolio.carryFee?.toLocaleString(i18n.language)}`} icon={moneybagIcon} />
                    </Box>
                </>
                :
                <>
                    {renderTitle(t('PortfolioPageItemSectorDistribution'), slidersIcon)}
                    <Box display={"flex"} flexWrap={"wrap"} gap={"8px 9px"}>
                        {sectors.slice(0, Math.min(5, portfolio.sectorDistribution.length)).map((s) =>
                            <LeoTag key={`sector-${s.sector}`} value={s.proportion} text={s.sector} icon={s.icon} />)}
                    </Box>
                    <Divider />
                    {renderTitle(t('PortfolioPageItemStageDistribution'), camemberIcon)}
                    <StageDistributionGraph data={portfolio.stageDistribution} />
                    <Divider />
                    {renderTitle(t('PortfolioPageItemGeographicDistribution'), worldIcon)}
                    <GeographicDistributionGraph distribution={portfolio.geographicDistribution} />
                </>}
            <Divider />
            {renderTitle(t('PortfolioPageItemFundAllocation'), donationIcon)}
            <FundAllocationGraph allocations={portfolio.fundAllocation} />
        </Box>
        {portfolio.status === PortfolioStatus.Draft && !isAddToExisting && <Box display={"flex"} justifyContent={"end"}>
            <Button startIcon={<PublishIcon />} variant="contained" onClick={openPublish}>Publish</Button>
        </Box>}
    </Box>
}

export default PortfolioItem;